import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Target, TrendingUp, TrendingDown, RefreshCw, Zap, ChartBar, Award, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SEGMENT_LABELS = {
  index_options: 'Index Options & Futures',
  stock_fno: 'Stock F&O',
  equity: 'Equity Cash',
};

const OUTCOME_COLORS = {
  T2_HIT: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  T1_HIT: 'bg-green-100 text-green-800 border-green-300',
  EXPIRED_WIN: 'bg-blue-100 text-blue-800 border-blue-300',
  EXPIRED_LOSS: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  SL_HIT: 'bg-red-100 text-red-800 border-red-300',
  INVALID: 'bg-gray-100 text-gray-500 border-gray-300',
};

const fmt = (n, dp = 1) => (n === null || n === undefined ? '—' : Number(n).toFixed(dp));
const dateStr = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

const KpiCard = ({ icon: Icon, label, value, color = 'text-gray-900' }) => (
  <Card>
    <CardContent className="p-4 text-center">
      <Icon className="w-5 h-5 mx-auto mb-1 text-gray-500" />
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </CardContent>
  </Card>
);

const AITradeIdeasAccuracyTab = () => {
  const [stats, setStats] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'generate' | 'evaluate'

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        fetch(`${API_URL}/api/admin/ai-trade-ideas/accuracy?days=${days}`).then(r => r.json()),
        fetch(`${API_URL}/api/admin/ai-trade-ideas/list?days=${days}`).then(r => r.json()),
      ]);
      setStats(s);
      setIdeas(i.ideas || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [days]);  // fetchAll intentionally omitted; days trigger is enough

  const triggerGenerate = async () => {
    if (!window.confirm('Generate a fresh AI Trade Idea for each of the 3 segments right now? Runs in background — refresh in ~90s to see new ideas.')) return;
    setBusy('generate');
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-trade-ideas/generate-now`, { method: 'POST' });
      const d = await res.json();
      window.alert(d.message || 'Generation kicked off');
    } catch (err) {
      window.alert('Error: ' + err.message);
    } finally { setBusy(null); }
  };

  const triggerEvaluate = async () => {
    if (!window.confirm('Score all expired-but-unevaluated ideas using yfinance OHLC data now?')) return;
    setBusy('evaluate');
    try {
      const res = await fetch(`${API_URL}/api/admin/ai-trade-ideas/evaluate-now`, { method: 'POST' });
      const d = await res.json();
      window.alert(`Evaluator done — scanned: ${d.scanned}, evaluated: ${d.evaluated}, errors: ${d.errors}`);
      fetchAll();
    } catch (err) {
      window.alert('Error: ' + err.message);
    } finally { setBusy(null); }
  };

  const segmentStats = useMemo(() => stats?.by_segment || {}, [stats]);
  const overall = stats?.overall || {};
  const publicEligible = stats?.public_display_eligible;

  return (
    <div className="space-y-4">
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Window:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
            data-testid="ai-accuracy-days-filter"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchAll} data-testid="ai-accuracy-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={triggerGenerate} disabled={busy === 'generate'} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-accuracy-generate-btn">
            <Zap className="w-4 h-4 mr-1" />
            {busy === 'generate' ? 'Starting…' : 'Generate Now'}
          </Button>
          <Button size="sm" onClick={triggerEvaluate} disabled={busy === 'evaluate'} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ai-accuracy-evaluate-btn">
            <Play className="w-4 h-4 mr-1" />
            {busy === 'evaluate' ? 'Scoring…' : 'Evaluate Now'}
          </Button>
        </div>
      </div>

      {/* Public-eligibility banner */}
      {publicEligible ? (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-emerald-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-900">Track record now displays publicly on landing page</p>
              <p className="text-xs text-emerald-700">Overall win rate <b>{overall.win_rate_pct}%</b> over {overall.total} ideas — meets the 30-idea sample + 70% win-rate threshold.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-3 flex items-center gap-2 text-xs text-gray-600">
            <ChartBar className="w-4 h-4 text-gray-500" />
            <span>
              Public track record hidden — needs <b>≥30 evaluated ideas</b> AND <b>≥70% win rate</b>. Currently {overall.total || 0} evaluated · {fmt(overall.win_rate_pct, 1)}% win rate.
            </span>
          </CardContent>
        </Card>
      )}

      {/* Overall KPIs */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Overall (last {days} days)</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <KpiCard icon={Target} label="Total Ideas" value={overall.total || 0} />
          <KpiCard icon={TrendingUp} label="Win Rate" value={`${fmt(overall.win_rate_pct, 1)}%`} color={overall.win_rate_pct >= 70 ? 'text-emerald-600' : overall.win_rate_pct >= 50 ? 'text-amber-600' : 'text-red-600'} />
          <KpiCard icon={Award} label="T2 Hit %" value={`${fmt(overall.t2_hit_pct, 1)}%`} color="text-emerald-600" />
          <KpiCard icon={TrendingDown} label="SL Hit %" value={`${fmt(overall.sl_hit_pct, 1)}%`} color="text-red-600" />
          <KpiCard icon={ChartBar} label="Avg P&L (close)" value={`${overall.avg_pnl_pct >= 0 ? '+' : ''}${fmt(overall.avg_pnl_pct, 2)}%`} color={overall.avg_pnl_pct >= 0 ? 'text-emerald-600' : 'text-red-600'} />
          <KpiCard icon={Target} label="Wins" value={overall.wins || 0} color="text-emerald-600" />
        </div>
      </div>

      {/* By-segment breakdown */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">By segment</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {['index_options', 'stock_fno', 'equity'].map(seg => {
            const s = segmentStats[seg] || {};
            return (
              <Card key={seg}>
                <CardHeader className="pb-1">
                  <CardTitle className="text-sm">{SEGMENT_LABELS[seg]}</CardTitle>
                </CardHeader>
                <CardContent className="pt-2 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-medium">{s.total || 0}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Win rate</span><span className={`font-bold ${s.win_rate_pct >= 70 ? 'text-emerald-600' : s.win_rate_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{fmt(s.win_rate_pct, 1)}%</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">T2 hit</span><span>{fmt(s.t2_hit_pct, 1)}%</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">SL hit</span><span className="text-red-600">{fmt(s.sl_hit_pct, 1)}%</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Avg P&L</span><span className={s.avg_pnl_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}>{s.avg_pnl_pct >= 0 ? '+' : ''}{fmt(s.avg_pnl_pct, 2)}%</span></div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Ideas list */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">All ideas ({ideas.length}) — sorted newest first</p>
        {loading ? (
          <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : ideas.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">
            No AI Trade Ideas generated yet. Click <b>Generate Now</b> above to create the first batch.
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Segment</th>
                  <th className="text-left px-3 py-2">Instrument · Strategy</th>
                  <th className="text-left px-3 py-2">Bias</th>
                  <th className="text-right px-3 py-2">Entry</th>
                  <th className="text-right px-3 py-2">T1</th>
                  <th className="text-right px-3 py-2">T2</th>
                  <th className="text-right px-3 py-2">SL</th>
                  <th className="text-center px-3 py-2">Outcome</th>
                  <th className="text-right px-3 py-2">P&L%</th>
                </tr>
              </thead>
              <tbody>
                {ideas.map(i => (
                  <tr key={i.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`ai-idea-row-${i.id}`}>
                    <td className="px-3 py-2 text-gray-500">{dateStr(i.generated_at)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-block bg-gray-100 rounded px-1.5 py-0.5 text-[10px] font-medium">
                        {SEGMENT_LABELS[i.segment] || i.segment}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{i.instrument}</div>
                      <div className="text-[10px] text-gray-500">{i.strategy}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={
                        i.bias === 'bullish' ? 'border-emerald-300 text-emerald-700' :
                        i.bias === 'bearish' ? 'border-red-300 text-red-700' :
                        'border-gray-300 text-gray-600'
                      }>{i.bias}</Badge>
                    </td>
                    <td className="text-right px-3 py-2 font-mono">{fmt(i.entry_price, 1)}</td>
                    <td className="text-right px-3 py-2 font-mono text-emerald-700">{fmt(i.target_1, 1)}</td>
                    <td className="text-right px-3 py-2 font-mono text-emerald-800">{fmt(i.target_2, 1)}</td>
                    <td className="text-right px-3 py-2 font-mono text-red-700">{fmt(i.stop_loss, 1)}</td>
                    <td className="px-3 py-2 text-center">
                      {i.evaluated ? (
                        <Badge className={`border ${OUTCOME_COLORS[i.outcome] || OUTCOME_COLORS.INVALID}`}>{i.outcome}</Badge>
                      ) : (
                        <span className="text-gray-400 text-[10px]">Pending</span>
                      )}
                    </td>
                    <td className={`text-right px-3 py-2 font-mono font-bold ${i.outcome_details?.pnl_pct_close_basis > 0 ? 'text-emerald-600' : i.outcome_details?.pnl_pct_close_basis < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {i.outcome_details?.pnl_pct_close_basis !== undefined && i.outcome_details?.pnl_pct_close_basis !== null
                        ? `${i.outcome_details.pnl_pct_close_basis >= 0 ? '+' : ''}${fmt(i.outcome_details.pnl_pct_close_basis, 2)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Best / Worst 5 */}
      {stats?.best_5 && stats.best_5.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card>
            <CardHeader className="pb-1"><CardTitle className="text-sm text-emerald-700">Top 5 Winning Ideas</CardTitle></CardHeader>
            <CardContent className="pt-2 space-y-1 text-xs">
              {stats.best_5.map((x, i) => (
                <div key={i} className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-700">{x.instrument} <span className="text-gray-400">({x.segment})</span></span>
                  <span className="font-bold text-emerald-600">+{fmt(x.pnl, 2)}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1"><CardTitle className="text-sm text-red-700">Worst 5 Ideas</CardTitle></CardHeader>
            <CardContent className="pt-2 space-y-1 text-xs">
              {stats.worst_5.map((x, i) => (
                <div key={i} className="flex justify-between border-b border-gray-100 py-1">
                  <span className="text-gray-700">{x.instrument} <span className="text-gray-400">({x.segment})</span></span>
                  <span className="font-bold text-red-600">{fmt(x.pnl, 2)}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AITradeIdeasAccuracyTab;
